/* Circadia cookie-consent banner — behaviour (two-view redesign).
   ============================================================================
   Client-Web Analytics + Law-25 Consent (S1).

   Wires the banner markup (snippets/consent-banner.liquid) to the single consent
   source (consent-config.js). On a choice it does all THREE legs of the locked
   dual-storage decision (UNCHANGED by this redesign):
     1. writes the first-party cookie (the harness fast-path),
     2. mirrors to Shopify Customer Privacy via setTrackingConsent (native pixels
        + the originally-built read path honour it),
     3. POSTs a durable audit row to /apps/dashboard/consent.

   UI: two views.
     VIEW 1 (simple): Accept all / Accept essentials / Customize + an X close.
       - Accept all       -> { analytics:true,  marketing:true,  preferences:true }
       - Accept essentials-> { analytics:false, marketing:false, preferences:false }
       - Customize        -> switches to VIEW 2 (writes nothing)
       - X / Escape       -> POLICY-AWARE implied close (see impliedClose)
     VIEW 2 (customize): the 3 toggles + Save my choices + Back.
       - Save my choices  -> the per-toggle state (readToggles)
       - Back             -> VIEW 1
     Law-25: Accept all and Accept essentials are equally prominent in the markup.

   Loaded with `defer` AFTER consent-config.js and analytics-emit.js. */

(function () {
  'use strict';

  // Kept in sync with the copy rendered in snippets/consent-banner.liquid; sent as
  // consent_text_shown on the audit row (the wording the visitor actually saw).
  var CONSENT_TEXT =
    'We use cookies for analytics, marketing, and preferences. Choose which you allow. You can change this anytime.';

  var previouslyFocused = null;

  // Whether the banner is currently open OVER a saved explicit choice (a
  // "re-open to review" via the footer "Cookie preferences" control) rather
  // than as the first-visit prompt. Captured at open time (see setOpenMode).
  //
  // This is the guard for the coupled Law-25/PIPEDA bug: an X/Escape close on a
  // REVIEW must be a pure dismiss — it must NEVER overwrite the saved choice
  // with an implied grant (withdrawal must not silently upgrade to consent).
  // Only the first-visit prompt (no explicit choice yet) treats a close as an
  // implied choice.
  var openedOverExplicitChoice = false;

  function hasExplicitChoiceNow() {
    return !!(
      window.Circadia &&
      window.Circadia.consent &&
      window.Circadia.consent.hasExplicitChoice &&
      window.Circadia.consent.hasExplicitChoice()
    );
  }

  function applyToShopify(choice) {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.setTrackingConsent === 'function') {
        cp.setTrackingConsent(
          {
            analytics: choice.analytics === true,
            marketing: choice.marketing === true,
            preferences: choice.preferences === true,
          },
          function () {}
        );
      }
    } catch (_) {
      /* native CMP absent — the cookie + audit legs still apply */
    }
  }

  function postAudit(choice, opts) {
    try {
      var vid = null;
      // Reading an existing id is allowed without consent; a NEW id is minted only
      // when analytics was just granted (the harness self-gates).
      if (
        window.Circadia &&
        window.Circadia.analytics &&
        typeof window.Circadia.analytics.visitorId === 'function'
      ) {
        vid = window.Circadia.analytics.visitorId();
      }
      var ver =
        window.Circadia && window.Circadia.consent ? window.Circadia.consent.POLICY_VERSION : null;
      // P1-24 implied-vs-explicit marker on the durable audit row: an X/Escape
      // implied close is recorded as 'cookie_banner_implied_close'; an explicit
      // button choice (Accept all / Accept essentials / Save) stays 'cookie_banner'.
      // Lets consent_records distinguish an implied grant from an explicit one for
      // Law-25 defence — stored in the existing `source` column (no schema change).
      var source =
        opts && opts.implied === true ? 'cookie_banner_implied_close' : 'cookie_banner';
      fetch('/apps/dashboard/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify({
          visitor_id: vid,
          policy_version: ver,
          source: source,
          consent_text_shown: CONSENT_TEXT,
          categories: {
            analytics: choice.analytics === true,
            marketing: choice.marketing === true,
            preferences: choice.preferences === true,
          },
        }),
      }).catch(function () {
        /* fire-and-forget audit */
      });
    } catch (_) {
      /* never let the audit beacon throw */
    }
  }

  // P2-7 (Law-25): on an analytics REFUSAL, delete the client-readable tracking
  // identifiers so a withdrawal actually removes the ids — the server cannot do
  // it (the App Proxy strips Set-Cookie; see the backend's consent-ingest.ts).
  // Names + attributes mirror analytics-emit.js (circadia_vid / circadia_sid /
  // circadia_attr_first / circadia_attr_last), plus the legacy localStorage
  // mirror, which visitorId() would otherwise re-migrate into a fresh cookie.
  function clearTrackingIds() {
    try {
      var secure =
        window.location && window.location.protocol === 'https:' ? '; Secure' : '';
      ['circadia_vid', 'circadia_sid', 'circadia_attr_first', 'circadia_attr_last'].forEach(
        function (name) {
          document.cookie = name + '=; Max-Age=0; Path=/; SameSite=Lax' + secure;
        }
      );
      try {
        window.localStorage.removeItem('circadia_visitor_id');
      } catch (_) {
        /* storage blocked — cookies were still cleared */
      }
    } catch (_) {
      /* best-effort — never throw into the banner */
    }
  }

  function hide(root) {
    if (!root) return;
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    // Restore focus to whatever the visitor was on before the banner opened
    // (e.g. the footer "Cookie preferences" link).
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      try {
        previouslyFocused.focus();
      } catch (_) {
        /* element gone — ignore */
      }
    }
    previouslyFocused = null;
  }

  function show(root) {
    if (!root) return;
    previouslyFocused = document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    // Move focus into the dialog so AT announces it and the focus trap engages.
    try {
      root.focus();
    } catch (_) {
      /* older browsers — trap + controls still work */
    }
  }

  function persist(root, choice, opts) {
    try {
      // 1. Cookie FIRST so analyticsGranted() reflects the choice before step 3
      //    reads/mints the visitor id.
      if (window.Circadia && window.Circadia.consent) {
        window.Circadia.consent.writeCookie(choice, opts);
      }
      // 2. Mirror to Shopify Customer Privacy (native pixels honour it).
      applyToShopify(choice);
      // 3. Durable audit row (best-effort). opts.implied marks an X/Escape close.
      postAudit(choice, opts);
      // 3b. Analytics refused → delete the tracking identifiers (P2-7). AFTER
      //     postAudit, whose body already captured the id, so the withdrawal
      //     row stays attributable to the visitor it belongs to.
      if (choice.analytics !== true) clearTrackingIds();
      // 4. Hide + notify so the harness can fire the now-allowed page_viewed (S3).
      hide(root);
      try {
        window.dispatchEvent(new CustomEvent('circadia:consent-changed', { detail: choice }));
      } catch (_) {
        /* CustomEvent unsupported — choice still persisted */
      }
    } catch (_) {
      /* never let the banner throw into the page */
    }
  }

  function readToggles(root) {
    function checked(cat) {
      var node = root.querySelector('[data-consent-toggle="' + cat + '"]');
      return !!(node && node.checked);
    }
    return {
      analytics: checked('analytics'),
      marketing: checked('marketing'),
      preferences: checked('preferences'),
    };
  }

  // Re-sync the VIEW 2 toggles to the visitor's current effective consent. Called
  // every time the customize view opens — fixes the latent bug where a re-opened
  // banner showed stale toggle state (the pre-check used to run once at init).
  function syncToggles(root) {
    try {
      var snap =
        window.Circadia && window.Circadia.consent
          ? window.Circadia.consent.snapshot()
          : { analytics: false, marketing: false, preferences: false };
      ['analytics', 'marketing', 'preferences'].forEach(function (cat) {
        var node = root.querySelector('[data-consent-toggle="' + cat + '"]');
        if (node) node.checked = snap[cat] === true;
      });
    } catch (_) {
      /* leave markup defaults */
    }
  }

  function policyName() {
    return (window.Circadia && window.Circadia.consent && window.Circadia.consent.POLICY) || 'pipeda';
  }

  // The X / Escape close.
  //   FIRST-VISIT prompt (no explicit choice yet) = implied consent, POLICY-AWARE:
  //     pipeda -> only analytics may be implied; marketing/preferences stay false.
  //     law25  -> nothing may be implied; closing = refuse all non-essential.
  //   RE-OPEN over a saved choice = PURE DISMISS: hide, persist NOTHING. Closing
  //     a review must never overwrite the saved choice (a prior refusal stays
  //     refused; nothing is silently upgraded to granted). Explicit re-choices —
  //     Accept all / Accept essentials / Save — still persist as usual.
  function impliedClose(root) {
    if (openedOverExplicitChoice) {
      hide(root); // pure dismiss — the saved choice is untouched
      return;
    }
    if (policyName() === 'pipeda') {
      persist(root, { analytics: true, marketing: false, preferences: false }, { implied: true });
    } else {
      persist(root, { analytics: false, marketing: false, preferences: false }, { implied: true });
    }
  }

  // Keep the X's label + the visible note accurate for the active mode. On a
  // re-open (reviewing a saved choice) the close is a no-op, so the copy must
  // NOT claim closing accepts anything.
  function setPolicyCopy(root) {
    var note = root.querySelector('[data-consent-note]');
    var close = root.querySelector('[data-consent-close]');
    var noteText, closeLabel;
    if (openedOverExplicitChoice) {
      noteText = 'Closing keeps your current choices.';
      closeLabel = 'Close. Keeps your current choices.';
    } else if (policyName() === 'pipeda') {
      noteText = 'Closing accepts essential and analytics cookies only.';
      closeLabel = 'Close. Accepts essential and analytics cookies only.';
    } else {
      noteText = 'Closing declines all non-essential cookies.';
      closeLabel = 'Close. Declines all non-essential cookies.';
    }
    if (note) note.textContent = noteText;
    if (close) close.setAttribute('aria-label', closeLabel);
  }

  // Capture whether this open is a first-visit prompt or a re-open over a saved
  // choice, then refresh the close copy to match. Call at EVERY open.
  function setOpenMode(root) {
    openedOverExplicitChoice = hasExplicitChoiceNow();
    setPolicyCopy(root);
  }

  function showView(root, name) {
    var simple = root.querySelector('[data-cc-view="simple"]');
    var custom = root.querySelector('[data-cc-view="custom"]');
    var customizeBtn = root.querySelector('[data-consent-customize]');
    if (name === 'custom') {
      if (simple) simple.hidden = true;
      if (custom) custom.hidden = false;
      if (customizeBtn) customizeBtn.setAttribute('aria-expanded', 'true');
      syncToggles(root); // re-sync toggles to saved state EVERY time this view opens
      var firstToggle = custom && custom.querySelector('[data-consent-toggle]');
      if (firstToggle) {
        try { firstToggle.focus(); } catch (_) {}
      }
    } else {
      if (custom) custom.hidden = true;
      if (simple) simple.hidden = false;
      if (customizeBtn) customizeBtn.setAttribute('aria-expanded', 'false');
    }
  }

  // Visible, tabbable controls inside the banner (the hidden view is display:none,
  // so its controls have a null offsetParent and are excluded automatically).
  function focusables(root) {
    var nodes = root.querySelectorAll('button, [href], input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])');
    return Array.prototype.filter.call(nodes, function (el) {
      return !el.disabled && el.offsetParent !== null;
    });
  }

  function init() {
    var root = document.querySelector('[data-consent-banner]');
    if (!root) return;

    setOpenMode(root);
    showView(root, 'simple'); // default view (markup already hides custom)

    var on = function (sel, handler) {
      var node = root.querySelector(sel);
      if (node) node.addEventListener('click', handler);
    };

    on('[data-consent-accept]', function () {
      persist(root, { analytics: true, marketing: true, preferences: true });
    });
    on('[data-consent-essentials]', function () {
      persist(root, { analytics: false, marketing: false, preferences: false });
    });
    on('[data-consent-customize]', function () {
      showView(root, 'custom');
    });
    on('[data-consent-back]', function () {
      showView(root, 'simple');
      var c = root.querySelector('[data-consent-customize]');
      if (c) { try { c.focus(); } catch (_) {} }
    });
    on('[data-consent-save]', function () {
      persist(root, readToggles(root));
    });
    on('[data-consent-close]', function () {
      impliedClose(root);
    });

    // Focus trap + Escape (policy-aware close), active whenever focus is in the banner.
    root.addEventListener('keydown', function (e) {
      var key = e.key || '';
      if (key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        impliedClose(root);
        return;
      }
      if (key === 'Tab' || e.keyCode === 9) {
        var f = focusables(root);
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Show only when there is no current-version explicit choice yet.
    var hasChoice = !!(
      window.Circadia &&
      window.Circadia.consent &&
      window.Circadia.consent.hasExplicitChoice &&
      window.Circadia.consent.hasExplicitChoice()
    );
    if (hasChoice) hide(root);
    else show(root);

    // Re-open path: window.Circadia.consent.openBanner() or any [data-consent-open]
    // control (e.g. a footer "Cookie preferences" link). Re-open shows VIEW 1.
    function openBanner() {
      // Re-capture the mode on every open: if a saved choice already exists this
      // is a review, so the X/Escape close becomes a pure dismiss (never
      // overwrites the saved choice with an implied grant).
      setOpenMode(root);
      showView(root, 'simple');
      show(root);
    }
    if (window.Circadia && window.Circadia.consent) {
      window.Circadia.consent.openBanner = openBanner;
    }
    Array.prototype.forEach.call(document.querySelectorAll('[data-consent-open]'), function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        openBanner();
      });
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
